import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { RCContratistasAseguradoScreen } from '#app/components/cotizacion/screens/rc-contratistas/RCContratistasAseguradoScreen'
import { RCContratistasConfirmationScreen } from '#app/components/cotizacion/screens/rc-contratistas/RCContratistasConfirmationScreen'
import { RCContratistasContratistaScreen } from '#app/components/cotizacion/screens/rc-contratistas/RCContratistasContratistaScreen'
import { RCContratistasIntroScreen } from '#app/components/cotizacion/screens/rc-contratistas/RCContratistasIntroScreen'
import { RCContratistasTrabajoScreen } from '#app/components/cotizacion/screens/rc-contratistas/RCContratistasTrabajoScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendCotizacionRCContratistasEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizacion-rc-contratistas'

export const meta: Route.MetaFunction = () => [
	{ title: 'Cotización RC Contratistas - 100prevalor' },
	{
		name: 'description',
		content:
			'Cotizá tu seguro de Responsabilidad Civil para contratistas y construcción con 100prevalor',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as RCContratistasFormData

	try {
		await sendCotizacionRCContratistasEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

type RCContratistasFormData = {
	// Asegurado
	nombreAsegurado: string
	direccion: string
	telefono: string
	email: string
	// Trabajo
	nombreComitente: string
	actividadComitente: string
	descripcionTrabajo: string
	gruasMaquinarias: string
	duracionEstimada: string
	direccionSitio: string
	dentroFueraPredio: string
	descripcionSitio: string
	// Contratista
	antiguedadRamo: string
	facturacionAnual: string
	cantidadEmpleados: string
	experienciaSiniestral: string
	montoContrato: string
	empleadosAsignados: string
	bienesBajoCustodia: string
	trabajosSimilares: string
	subcontratistas: string
	esPetrolero: boolean
	categoriaPetrolera: string
	aseguradoraActual: string
	vigenciaPoliza: string
	coberturasSumas: string
}

export default function CotizacionRCContratistas() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<Partial<RCContratistasFormData>>({})

	const totalSteps = 5

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const getProgressPercentage = () => {
		if (step <= 1) return 0
		const progressMap: Record<number, number> = {
			2: 25,
			3: 50,
			4: 75,
			5: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 2

	const handleNext = async (stepData?: Partial<RCContratistasFormData>) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		if (step === 4) {
			const finalData = { ...formData, ...stepData }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/cotizacion-rc-contratistas', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email RC Contratistas enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email RC Contratistas')
				}
			} catch (error) {
				console.error('❌ Error al enviar email RC Contratistas:', error)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setFormData({})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <RCContratistasIntroScreen onNext={() => handleNext()} />
			case 2:
				return (
					<RCContratistasAseguradoScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 3:
				return (
					<RCContratistasTrabajoScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 4:
				return (
					<RCContratistasContratistaScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 5:
				return (
					<RCContratistasConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as RCContratistasFormData}
					/>
				)
			default:
				return <RCContratistasIntroScreen onNext={() => handleNext()} />
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="crane" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="crane" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
